/**
 * ShortDebriefingGuide — condensed single-page debriefing reference
 *
 * Default view for debriefing guides. Flat overview layout matching
 * the source document (ORCA360 Debriefing Guide Short version.docx).
 * All ▶ expandable links start collapsed.
 */

import { useState, useCallback } from 'react';
import type {
  DebriefingGuideRow,
  PartB_DebriefingFramework,
  PartD_InstructorNotes,
} from '../../types/debriefing-guide';
import { selectShortGuideItems } from '../../lib/short-debriefing-guide.utils';
import ExpandableSection from './ExpandableSection';
import UASCard from './UASCard';
import EventCard from './EventCard';
import QualityGateBox from './QualityGateBox';
import InstructorNotesEditor from './InstructorNotesEditor';

interface ShortDebriefingGuideProps {
  guide: DebriefingGuideRow;
  onGuideChange: (guide: DebriefingGuideRow) => void;
  onSwitchToFull: () => void;
  readOnly?: boolean;
  sessionId: string;
  instructorNotes?: string | null;
}

export default function ShortDebriefingGuide({
  guide,
  onGuideChange,
  onSwitchToFull: _onSwitchToFull,
  readOnly,
  sessionId,
  instructorNotes,
}: ShortDebriefingGuideProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const partB = guide.part_b as PartB_DebriefingFramework;
  const partD = guide.part_d as PartD_InstructorNotes | null;

  const allUas = partB?.uasFocusedRCA ?? [];
  const allEvents = partB?.eventFocusedExploration ?? [];
  const displayItems = selectShortGuideItems(allUas, allEvents);
  const strategy = partB?.crewDefinedStrategy;

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const handleQualityToggle = useCallback(
    (key: 'behavioral' | 'transferable' | 'competencyLinked' | 'crewOwned') => {
      if (readOnly || !strategy) return;
      const updatedStrategy = {
        ...strategy,
        qualityCheck: {
          ...strategy.qualityCheck,
          [key]: !strategy.qualityCheck[key],
        },
      };
      const updatedPartB = { ...partB, crewDefinedStrategy: updatedStrategy };
      onGuideChange({ ...guide, part_b: updatedPartB });
    },
    [guide, partB, strategy, onGuideChange, readOnly],
  );

  return (
    <div className="space-y-2">
      {/* ─── Global actions ─── */}
      <div>
        <ExpandableSection
          label="Click to view or edit instructor notes"
          expanded={!!expandedSections.instructor_notes_top}
          onToggle={() => toggleSection('instructor_notes_top')}
        >
          <InstructorNotesEditor
            sessionId={sessionId}
            instructorNotes={instructorNotes ?? null}
            readOnly={readOnly}
          />
        </ExpandableSection>
      </div>

      {/* ─── 1. Opening (Self-Assessment) ─── */}
      <div>
        <SectionHeading number={1} title="Opening (Self-Assessment)" />
        <div className="mt-1 ml-1">
          <p className="text-sm text-gray-700 mb-2">
            Crew self-assessment using open questions. Do not lecture — use guided discovery.
          </p>
          <ExpandableSection
            label="Click to view suggested facilitator prompts"
            expanded={!!expandedSections.facilitator_prompts}
            onToggle={() => toggleSection('facilitator_prompts')}
          >
            {partB?.openingSelfAssessment?.prompts ? (
              <ul className="list-disc list-inside space-y-1 bg-blue-50 p-3 rounded">
                {partB.openingSelfAssessment.prompts.map((prompt, idx) => (
                  <li key={idx} className="text-sm text-blue-800">
                    &ldquo;{prompt}&rdquo;
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState message="Facilitator prompts will be available after assessment generation." />
            )}
          </ExpandableSection>
        </div>
      </div>

      {/* ─── 2. UAS-Focused Root Cause Analysis (if applicable) ─── */}
      <div>
        <SectionHeading number={2} title="UAS-Focused Root Cause Analysis (if applicable)" />
        <div className="mt-1 ml-1">
          {displayItems.uas_items.length > 0 ? (
            <>
              {displayItems.uas_items.map((uas, idx) => (
                <UASCard
                  key={idx}
                  uas={uas}
                  index={idx}
                  expandedSections={expandedSections}
                  onToggleSection={toggleSection}
                />
              ))}
              {displayItems.has_more_uas && (
                <ExpandableSection
                  label={`Click to expand full UAS log`}
                  expanded={!!expandedSections.full_uas_log}
                  onToggle={() => toggleSection('full_uas_log')}
                >
                  <div className="space-y-3">
                    {allUas.slice(displayItems.uas_items.length).map((uas, idx) => (
                      <UASCard
                        key={idx + displayItems.uas_items.length}
                        uas={uas}
                        index={idx + displayItems.uas_items.length}
                        expandedSections={expandedSections}
                        onToggleSection={toggleSection}
                      />
                    ))}
                  </div>
                </ExpandableSection>
              )}
            </>
          ) : (
            <EmptyState message="Nil" />
          )}
        </div>
      </div>

      {/* ─── 3. Event-Focused Exploration (per event) ─── */}
      <div>
        <SectionHeading number={3} title="Event-Focused Exploration (per event)" />
        <div className="mt-1 ml-1">
          {displayItems.events.length > 0 ? (
            <>
              {displayItems.events.map((event, idx) => (
                <EventCard
                  key={idx}
                  event={event}
                  index={idx}
                  expandedSections={expandedSections}
                  onToggleSection={toggleSection}
                />
              ))}
              {displayItems.has_more_events && (
                <ExpandableSection
                  label="Click to expand full event observation log with all events"
                  expanded={!!expandedSections.full_event_log}
                  onToggle={() => toggleSection('full_event_log')}
                >
                  <div className="space-y-3">
                    {allEvents.slice(displayItems.events.length).map((event, idx) => (
                      <EventCard
                        key={idx + displayItems.events.length}
                        event={event}
                        index={idx + displayItems.events.length}
                        expandedSections={expandedSections}
                        onToggleSection={toggleSection}
                      />
                    ))}
                  </div>
                </ExpandableSection>
              )}
            </>
          ) : (
            <EmptyState message="Nil" />
          )}
        </div>
      </div>

      {/* ─── Part E: Instructor Notes (AI-generated self-reflection from long guide) ─── */}
      <ExpandableSection
        label="Click to view or edit instructor notes"
        expanded={!!expandedSections.instructor_notes_mid}
        onToggle={() => toggleSection('instructor_notes_mid')}
      >
        {partD ? (
          <div className="space-y-3 bg-red-50/30 border border-red-200/50 rounded-lg p-4">
            <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">
              Confidential — Instructor Only
            </span>
            {partD.calibrationReflections && (
              <div>
                <h5 className="text-xs font-semibold text-steel mb-1">Calibration Reflections</h5>
                <p className="text-sm text-gray-700">{partD.calibrationReflections}</p>
              </div>
            )}
            {partD.facilitationImprovementPoints && (
              <div>
                <h5 className="text-xs font-semibold text-steel mb-1">Facilitation Improvement Points</h5>
                <p className="text-sm text-gray-700">{partD.facilitationImprovementPoints}</p>
              </div>
            )}
            {partD.iecClassifications?.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-steel mb-1">IEC Self-Reflection</h5>
                {partD.iecClassifications.map((iec, i) => (
                  <p key={i} className="text-sm text-gray-700 pl-3 border-l-2 border-indigo-200 mb-1">
                    <span className="font-medium text-indigo-700">{iec.iec}:</span> {iec.reflection}
                  </p>
                ))}
              </div>
            )}
            {partD.orcaAlignmentObservations && (
              <div>
                <h5 className="text-xs font-semibold text-steel mb-1">ORCA Alignment Observations</h5>
                <p className="text-sm text-gray-700">{partD.orcaAlignmentObservations}</p>
              </div>
            )}
          </div>
        ) : (
          <EmptyState message="Instructor notes will be available after assessment generation." />
        )}
      </ExpandableSection>

      {/* ─── 4. Takeaways & Future Strategies ─── */}
      <div>
        <SectionHeading number={4} title="Takeaways & Future Strategies" />
        <div className="mt-1 ml-1">
          <p className="text-sm text-gray-700 mb-3">
            Crew defines a behavioural, transferable, competency-linked strategy — not a checklist.
          </p>

          {strategy ? (
            <div className="space-y-3">
              {/* Takeaways placeholder */}
              <div className="border-l-[3px] border-l-[#BF4E14] bg-white rounded-r-lg border border-l-0 border-steel/15 p-4">
                <p className="text-sm text-gray-900">
                  &ldquo;{strategy.statement}&rdquo;
                </p>
              </div>

              {/* Quality Gate — static text line */}
              <p className="text-sm text-gray-700">
                Quality Gate: It shall be Behavioural, Transferable, Competency-linked and with crew member ownership
              </p>

              <ExpandableSection
                label="Click to view quality gate checklist & strategy examples"
                expanded={!!expandedSections.quality_gate_checklist}
                onToggle={() => toggleSection('quality_gate_checklist')}
              >
                <div className="space-y-3">
                  {strategy.qualityCheck && (
                    <QualityGateBox
                      qualityCheck={strategy.qualityCheck}
                      onToggle={handleQualityToggle}
                      readOnly={readOnly}
                    />
                  )}
                  <div className="bg-gray-50 rounded p-3 text-sm space-y-2">
                    <p className="text-steel">
                      A high-quality crew strategy should meet all four criteria:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      <li><strong>Behavioural:</strong> Describes specific actions, not vague intentions</li>
                      <li><strong>Transferable:</strong> Applicable beyond this specific scenario</li>
                      <li><strong>Competency-linked:</strong> Maps to at least one ICAO competency</li>
                      <li><strong>Crew-owned:</strong> Agreed and articulated by the crew themselves</li>
                    </ul>
                    <p className="text-steel italic mt-2">
                      Example: &ldquo;In future operations, the crew agrees to verbalise energy state
                      at each 500ft increment below 2000ft during approach.&rdquo;
                    </p>
                  </div>
                </div>
              </ExpandableSection>
            </div>
          ) : (
            <EmptyState message="Takeaways will be available after assessment generation." />
          )}
        </div>
      </div>
    </div>
  );
}

/** Flat section heading — navy text, no card wrapper */
function SectionHeading({ number, title }: { number: number; title: string }) {
  return (
    <h3 className="text-sm font-bold text-[#1B3A5C] border-b border-steel/10 pb-1">
      {number}. {title}
    </h3>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-sm text-steel italic py-2">{message}</p>
  );
}
